import { useEffect, useState } from 'react'

// V2-6f2 §2.1: `useTitleSearch` moved out of `AddPanel.jsx`, unchanged, so
// there is ONE search client rather than two (CLAUDE.md gotcha 21) --
// V2-6h2 (Link the next season, on the title page) imports it next.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 DEBOUNCED, AND AT MOST ONE IN-FLIGHT REQUEST
// ═══════════════════════════════════════════════════════════════════════════
//
// `useTitleSearch` waits `DEBOUNCE_MS` of no further typing before asking,
// and cancels (`AbortController`) whatever it last asked the instant the
// query changes again — so a member typing a whole title never produces
// more than one request in flight, and a fast typist's earlier, now-stale
// request can never overwrite a later, correct answer (the classic
// out-of-order-response race a debounce alone does not fix).

const SEARCH_ENDPOINT = '/api/watch/search'
export const MIN_QUERY_LENGTH = 2
export const DEBOUNCE_MS = 400

const GENERIC_SEARCH_ERROR = "Couldn't search right now. Try again in a moment."
const RATE_LIMITED_FALLBACK = 'That’s a lot of searches at once. Give it a minute.'

function idleSearchState() {
  return { status: 'idle', results: [], providers: null, message: null }
}

/**
 * `query`'s own search lifecycle. `fetchImpl` is injectable for tests, the
 * same convention `watchRequests.js`/`watchArchive.js` already use.
 */
export function useTitleSearch(query, fetchImpl = globalThis.fetch) {
  const [state, setState] = useState(idleSearchState)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length < MIN_QUERY_LENGTH) {
      setState(idleSearchState())
      return undefined
    }

    let cancelled = false
    const controller = new AbortController()
    setState((previous) => ({ ...previous, status: 'loading' }))

    const timer = window.setTimeout(async () => {
      let response
      try {
        response = await fetchImpl(`${SEARCH_ENDPOINT}?q=${encodeURIComponent(trimmed)}`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        })
      } catch (error) {
        if (cancelled || error?.name === 'AbortError') return
        setState({ status: 'error', results: [], providers: null, message: GENERIC_SEARCH_ERROR })
        return
      }
      if (cancelled) return

      if (response.status === 429) {
        const text = await response.text().catch(() => '')
        setState({
          status: 'limited',
          results: [],
          providers: null,
          message: text.trim() !== '' ? text.trim() : RATE_LIMITED_FALLBACK,
        })
        return
      }
      if (!response.ok) {
        setState({ status: 'error', results: [], providers: null, message: GENERIC_SEARCH_ERROR })
        return
      }

      const body = await response.json().catch(() => null)
      if (!body || !Array.isArray(body.results)) {
        setState({ status: 'error', results: [], providers: null, message: GENERIC_SEARCH_ERROR })
        return
      }
      setState({ status: 'ready', results: body.results, providers: body.providers ?? null, message: null })
    }, DEBOUNCE_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [query, fetchImpl])

  return state
}
